/**
 * Sponsor profile view - declarative React component.
 * 
 * Elm-ish architecture: This component is THIN.
 * - No business logic
 * - No state mutations
 * - No API calls
 * - Only renders based on model state
 * - Delegates all actions to callbacks from the program
 * 
 * Security posture:
 * - Identity-minimized form fields only (display_name, headline, bio, city_or_region)
 * - No collection of legal name, phone, address, employer, or social profiles
 * - All inputs are optional
 * 
 * Profile activation per REQ-125, REQ-126, REQ-127:
 * - Gender-first UX: goal disabled until gender selected
 * - Age validation: 18-120
 * - Missing activation fields displayed for inactive profiles
 */
import type { SponsorProfileModel } from './model';
import type { UseSponsorProfileProgramResult } from './useSponsorProfileProgram';
import type { ProfileFormData } from './model';
import { validateForm, ProfileFormErrors, fieldCodeToLabel } from './model';
import type { Gender, SponsorGoal } from '@/api/profiles';

/**
 * Props for the view component.
 */
export interface SponsorProfileViewProps {
  /** Current model state */
  model: SponsorProfileModel;
  /** Start editing mode */
  onStartEdit: () => void;
  /** Update form field */
  onUpdateForm: (form: ProfileFormData) => void;
  /** Save the profile */
  onSave: (form: ProfileFormData) => void;
  /** Cancel editing */
  onCancel: () => void;
  /** Retry after error */
  onRetry: () => void;
  /** Change gender (per REQ-126 - must be chosen first) */
  onGenderChanged?: (gender: Gender | undefined) => void;
  /** Change goal (per REQ-127 - only available after gender) */
  onGoalChanged?: (goal: SponsorGoal | undefined) => void;
  /** Change age (per REQ-125 - must be 18-120) */
  onAgeChanged?: (age: number | undefined) => void;
}

/**
 * Loading state for profile.
 */
export function LoadingState() {
  return (
    <div className="sponsor-profile-loading" data-testid="sponsor-profile-loading">
      <p>Loading sponsor profile...</p>
    </div>
  );
}

/**
 * Error state with retry option.
 */
function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="sponsor-profile-error" data-testid="sponsor-profile-error">
      <p className="error-text">{message}</p>
      <button className="retry-button" onClick={onRetry} data-testid="retry-button">
        Try Again
      </button>
    </div>
  );
}

/**
 * Empty state when no profile exists yet.
 */
function EmptyState({ onStartEdit }: { onStartEdit: () => void }) {
  return (
    <div className="sponsor-profile-empty" data-testid="sponsor-profile-empty">
      <p>You haven't created your sponsor profile yet.</p>
      <p className="empty-hint">Create a profile to help friends discover you.</p>
      <button 
        className="btn btn-primary"
        onClick={onStartEdit}
        data-testid="create-profile-button"
      >
        Create Sponsor Profile
      </button>
    </div>
  );
}

/**
 * Validation error display for a field.
 */
function FieldError({ message }: { message: string }) {
  if (!message) return null;
  return (
    <p className="field-error" role="alert" data-testid="field-error">
      {message}
    </p>
  );
}

/**
 * Missing activation fields list per REQ-125.
 * Displays user-friendly labels for missing fields.
 */
function MissingActivationFieldsList({ fields }: { fields: readonly string[] }) {
  if (fields.length === 0) return null;
  
  return (
    <div className="missing-activation-fields" data-testid="missing-activation-fields">
      <h4 className="missing-fields-title">Complete your profile to activate:</h4>
      <ul className="missing-fields-list">
        {fields.map((code) => (
          <li key={code} className="missing-field-item">
            {fieldCodeToLabel(code)}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Inactive profile banner per REQ-125.
 */
function InactiveProfileBanner({ profileStatus }: { profileStatus: 'inactive' | 'active' | 'blocked' | 'deleted' }) {
  if (profileStatus === 'active') return null;
  
  if (profileStatus === 'blocked' || profileStatus === 'deleted') {
    return (
      <div className="profile-status-banner status-inactive" data-testid="profile-status-banner">
        <p>Profile is {profileStatus}. Contact support for assistance.</p>
      </div>
    );
  }
  
  return (
    <div className="profile-status-banner status-incomplete" data-testid="profile-status-banner">
      <p>Your profile is incomplete. Fill in the required fields below to activate.</p>
    </div>
  );
}

/**
 * Active profile confirmation banner.
 */
function ActiveConfirmation() {
  return (
    <div className="saved-confirmation active-confirmation" role="status" data-testid="active-confirmation">
      <p>✓ Profile is now active! All required fields are complete.</p>
    </div>
  );
}

/**
 * Gender field per REQ-126 (must be chosen first).
 */
function GenderField({ 
  value, 
  onChange, 
  disabled,
  error 
}: { 
  value: Gender | undefined;
  onChange: (value: Gender | undefined) => void;
  disabled: boolean;
  error: string | null;
}) {
  return (
    <div className="form-group" data-testid="gender-field">
      <label className="form-label">
        Gender <span className="required-label">*</span>
      </label>
      <p className="field-hint">Choose your gender first, then select your goal.</p>
      <div className="gender-options">
        <label className="radio-label">
          <input
            type="radio"
            name="gender"
            value="male"
            checked={value === 'male'}
            onChange={() => onChange('male')}
            disabled={disabled}
            data-testid="gender-male"
          />
          <span>Male</span>
        </label>
        <label className="radio-label">
          <input
            type="radio"
            name="gender"
            value="female"
            checked={value === 'female'}
            onChange={() => onChange('female')}
            disabled={disabled}
            data-testid="gender-female"
          />
          <span>Female</span>
        </label>
      </div>
      <FieldError message={error} />
    </div>
  );
}

/**
 * Goal field per REQ-127 (only available after gender).
 */
function GoalField({ 
  value, 
  onChange, 
  disabled,
  canSelect,
  error 
}: { 
  value: SponsorGoal | undefined;
  onChange: (value: SponsorGoal | undefined) => void;
  disabled: boolean;
  canSelect: boolean;
  error: string | null;
}) {
  if (!canSelect) {
    return (
      <div className="form-group" data-testid="goal-field">
        <label className="form-label">
          Goal
        </label>
        <p className="field-hint field-disabled">Select your gender first to choose a goal.</p>
      </div>
    );
  }
  
  return (
    <div className="form-group" data-testid="goal-field">
      <label className="form-label">
        Goal <span className="required-label">*</span>
      </label>
      <p className="field-hint">What are you looking for?</p>
      <select
        className="form-select"
        value={value || ''}
        onChange={(e) => onChange(e.target.value ? e.target.value as SponsorGoal : undefined)}
        disabled={disabled}
        data-testid="goal-select"
      >
        <option value="">Select a goal</option>
        <option value="wealthy_seeker">Looking for wealth-building opportunities</option>
      </select>
      <FieldError message={error} />
    </div>
  );
}

/**
 * Age field per REQ-125 (must be 18-120).
 */
function AgeField({ 
  value, 
  onChange, 
  disabled,
  error 
}: { 
  value: number | undefined;
  onChange: (value: number | undefined) => void;
  disabled: boolean;
  error: string | null;
}) {
  return (
    <div className="form-group" data-testid="age-field">
      <label htmlFor="age" className="form-label">
        Age <span className="required-label">*</span>
      </label>
      <input
        id="age"
        type="number"
        name="age"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value ? parseInt(e.target.value, 10) : undefined)}
        placeholder="Your age"
        className="form-input"
        min={18}
        max={120}
        disabled={disabled}
        data-testid="age-input"
      />
      <p className="field-hint">Must be between 18 and 120.</p>
      <FieldError message={error} />
    </div>
  );
}

/**
 * Profile view (read-only display).
 */
function ProfileView({ 
  profile,
  onStartEdit 
}: { 
  profile: SponsorProfileModel['profile'];
  onStartEdit: () => void;
}) {
  if (!profile) return null;
  
  return (
    <div className="sponsor-profile-view" data-testid="sponsor-profile-view">
      <div className="profile-display">
        <h3 className="profile-display-name" data-testid="profile-display-name">
          {profile.display_name || 'Anonymous Sponsor'}
        </h3>
        
        {profile.headline && (
          <p className="profile-headline" data-testid="profile-headline">
            {profile.headline}
          </p>
        )}
        
        {profile.bio && (
          <p className="profile-bio" data-testid="profile-bio">
            {profile.bio}
          </p>
        )}
        
        {profile.city_or_region && (
          <div className="profile-location">
            <span className="location-icon">📍</span>
            <span className="location-value">{profile.city_or_region}</span>
          </div>
        )}
        
        <div className="profile-updated">
          <span>Profile updated {formatRelativeTime(profile.updated_at)}</span>
        </div>
      </div>
      
      <button 
        className="btn btn-secondary"
        onClick={onStartEdit}
        data-testid="edit-profile-button"
      >
        Edit Sponsor Profile
      </button>
    </div>
  );
}

/**
 * Profile form for editing with activation fields.
 */
function ProfileForm({
  form,
  errors,
  isSaving,
  profileStatus,
  missingActivationFields,
  canSelectGoal,
  onUpdateForm,
  onSave,
  onCancel,
  onGenderChanged,
  onGoalChanged,
  onAgeChanged,
  ageError,
}: {
  form: ProfileFormData;
  errors: ProfileFormErrors;
  isSaving: boolean;
  profileStatus: 'inactive' | 'active' | 'blocked' | 'deleted';
  missingActivationFields: readonly string[];
  canSelectGoal: boolean;
  onUpdateForm: (form: ProfileFormData) => void;
  onSave: () => void;
  onCancel: () => void;
  onGenderChanged?: (gender: Gender | undefined) => void;
  onGoalChanged?: (goal: SponsorGoal | undefined) => void;
  onAgeChanged?: (age: number | undefined) => void;
  ageError?: string | null;
}) {
  const isNotActive = profileStatus !== 'active';
  
  const handleChange = (field: keyof ProfileFormData) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    onUpdateForm({ ...form, [field]: e.target.value });
  };
  
  return (
    <div className="sponsor-profile-form" data-testid="sponsor-profile-form">
      {/* Activation section header */}
      <div className="section-header">
        <h3 className="section-title">Sponsor Profile</h3>
        <p className="section-description">
          {isNotActive 
            ? 'Complete your profile to unlock all features.' 
            : 'Your public profile that friends will see in discovery.'}
        </p>
      </div>
      
      {/* Inactive profile banner */}
      {isNotActive && <InactiveProfileBanner profileStatus={profileStatus} />}
      
      {/* Missing activation fields */}
      {isNotActive && missingActivationFields.length > 0 && (
        <MissingActivationFieldsList fields={missingActivationFields} />
      )}
      
      {/* Profile status badge - only show when active */}
      {!isNotActive && (
        <div className="profile-status-badge" data-testid="profile-status-badge">
          <span className="status-badge status-active">
            ✓ Active
          </span>
        </div>
      )}
      
      {/* Activation fields - gender first per REQ-126 */}
      <GenderField
        value={form.gender}
        onChange={onGenderChanged || (() => {})}
        disabled={isSaving}
        error={null}
      />
      
      <GoalField
        value={form.goal}
        onChange={onGoalChanged || (() => {})}
        disabled={isSaving}
        canSelect={canSelectGoal}
        error={null}
      />
      
      <AgeField
        value={form.age}
        onChange={onAgeChanged || (() => {})}
        disabled={isSaving}
        error={ageError || null}
      />
      
      {/* Basic profile fields */}
      <div className="form-group">
        <label htmlFor="display_name">Display Name</label>
        <input
          id="display_name"
          type="text"
          value={form.display_name}
          onChange={handleChange('display_name')}
          placeholder="How you'd like to appear to friends"
          maxLength={100}
          className={errors.display_name ? 'input-error' : ''}
          data-testid="display-name-input"
        />
        {errors.display_name && (
          <span className="field-error">{errors.display_name}</span>
        )}
      </div>
      
      <div className="form-group">
        <label htmlFor="headline">Headline</label>
        <input
          id="headline"
          type="text"
          value={form.headline}
          onChange={handleChange('headline')}
          placeholder="A short tagline about yourself"
          maxLength={200}
          className={errors.headline ? 'input-error' : ''}
          data-testid="headline-input"
        />
        {errors.headline && (
          <span className="field-error">{errors.headline}</span>
        )}
      </div>
      
      <div className="form-group">
        <label htmlFor="bio">Bio</label>
        <textarea
          id="bio"
          value={form.bio}
          onChange={handleChange('bio')}
          placeholder="Tell friends about yourself, your values, or what you're looking for"
          maxLength={1000}
          rows={4}
          className={errors.bio ? 'input-error' : ''}
          data-testid="bio-input"
        />
        <span className="char-count">{form.bio.length}/1000</span>
        {errors.bio && (
          <span className="field-error">{errors.bio}</span>
        )}
      </div>
      
      <div className="form-group">
        <label htmlFor="city_or_region">City/Region</label>
        <input
          id="city_or_region"
          type="text"
          value={form.city_or_region}
          onChange={handleChange('city_or_region')}
          placeholder="Your general location (no full address)"
          maxLength={100}
          className={errors.city_or_region ? 'input-error' : ''}
          data-testid="city-or-region-input"
        />
        {errors.city_or_region && (
          <span className="field-error">{errors.city_or_region}</span>
        )}
      </div>
      
      <div className="form-actions">
        <button
          className="btn btn-primary"
          onClick={onSave}
          disabled={isSaving}
          data-testid="save-button"
        >
          {isSaving ? 'Saving...' : 'Save Profile'}
        </button>
        <button
          className="btn btn-secondary"
          onClick={onCancel}
          disabled={isSaving}
          data-testid="cancel-button"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

/**
 * Saved state confirmation.
 */
function SavedState({ 
  profile, 
  onStartEdit 
}: { 
  profile: NonNullable<SponsorProfileModel['profile']>;
  onStartEdit: () => void;
}) {
  return (
    <div className="sponsor-profile-saved" data-testid="sponsor-profile-saved">
      <p className="saved-message">✓ Profile saved successfully</p>
      <ProfileView profile={profile} onStartEdit={onStartEdit} />
    </div>
  );
}

/**
 * Format ISO timestamp to relative time.
 */
function formatRelativeTime(isoString: string): string {
  try {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'today';
    if (diffDays === 1) return 'yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return date.toLocaleDateString();
  } catch {
    return 'recently';
  }
}

/**
 * Main view component - routes to appropriate sub-component based on status.
 * 
 * This is the ONLY React component in the feature.
 * All logic is in the program hook; this just renders.
 */
export function SponsorProfileView(props: SponsorProfileViewProps) {
  const { model, onStartEdit, onUpdateForm, onSave, onCancel, onRetry } = props;
  const isInactive = model.profileStatus === 'inactive';
  const showInactiveBanner = model.profileStatus !== 'active';
  
  // Show loading state when idle
  if (model.status === 'idle' || model.status === 'loading') {
    return <LoadingState />;
  }
  
  // Show error state
  if (model.status === 'error') {
    return <ErrorState message={model.error || 'Unknown error'} onRetry={onRetry} />;
  }
  
  // Show empty state (no profile yet)
  if (model.status === 'empty') {
    return <EmptyState onStartEdit={onStartEdit} />;
  }
  
  // Show edit form if in edit mode
  if (model.pendingAction === 'StartEdit') {
    const errors = validateForm(model.form);
    const isSaving = model.status === 'saving';
    return (
      <ProfileForm
        form={model.form}
        errors={errors}
        isSaving={isSaving}
        profileStatus={model.profileStatus}
        missingActivationFields={model.missingActivationFields}
        canSelectGoal={model.canSelectGoal}
        onUpdateForm={onUpdateForm}
        onSave={() => onSave(model.form)}
        onCancel={onCancel}
        onGenderChanged={props.onGenderChanged}
        onGoalChanged={props.onGoalChanged}
        onAgeChanged={props.onAgeChanged}
        ageError={model.error}
      />
    );
  }
  
  // Show active confirmation if just saved and active
  if (model.status === 'active' && model.profile) {
    return (
      <div className="sponsor-profile-section" data-testid="sponsor-profile-section">
        <div className="section-header">
          <h3 className="section-title">Sponsor Profile</h3>
        </div>
        <ActiveConfirmation />
        <ProfileView profile={model.profile} onStartEdit={onStartEdit} />
      </div>
    );
  }
  
  // Show saved confirmation if just saved
  if (model.status === 'saved' && model.profile) {
    return <SavedState profile={model.profile} onStartEdit={onStartEdit} />;
  }
  
  // Show ready state (profile loaded, view mode)
  if (model.status === 'ready' && model.profile) {
    return <ProfileView profile={model.profile} onStartEdit={onStartEdit} />;
  }
  
  // Fallback (shouldn't reach here)
  return <LoadingState />;
}
